import React, { useCallback, useEffect, useState } from 'react';
import { ImageUploadIcon } from '@hugeicons-pro/core-stroke-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { useTranslation } from 'react-i18next';

export interface IDragAndDropOverlayProps {
  isEnabled?: boolean;
  label?: string;
  onDropFiles: (files: File[]) => void;
}

/**
 * Web-only drag-and-drop overlay. Covers the viewport while dragging files and
 * invokes onDropFiles with the dropped File list (images should be filtered by caller if needed).
 */
export function DragAndDropOverlay({ isEnabled = true, label, onDropFiles }: IDragAndDropOverlayProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const onDragOver = useCallback((e: DragEvent) => {
    if (!isEnabled) return;
    e.preventDefault();
    e.stopPropagation();
  }, [isEnabled]);

  const onDragEnter = useCallback((e: DragEvent) => {
    if (!isEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((c) => c + 1);
    setIsDragging(true);
  }, [isEnabled]);

  const onDragLeave = useCallback((e: DragEvent) => {
    if (!isEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((c) => {
      const next = Math.max(0, c - 1);
      if (next === 0) setIsDragging(false);
      return next;
    });
  }, [isEnabled]);

  const onDrop = useCallback((e: DragEvent) => {
    if (!isEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const dt = e.dataTransfer;
    if (!dt) return;
    const files = Array.from(dt.files || []);
    if (files.length > 0) onDropFiles(files as File[]);
  }, [isEnabled, onDropFiles]);

  useEffect(() => {
    if (!isEnabled) return;
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [isEnabled, onDragOver, onDragEnter, onDragLeave, onDrop]);

  if (!isEnabled || !isDragging) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
      <div className="pointer-events-none mx-4 w-full max-w-xl rounded-2xl border-2 border-dashed border-primary-500 bg-background-50/90 p-8 text-center shadow-xl">
        <div className="mb-3 flex w-full items-center justify-center">
          <HugeIconsWrapper icon={ImageUploadIcon} size={48} className="text-primary-500" />
        </div>
        <div className="text-lg font-semibold text-typography-700">{label || t('webDrop.dropToUpload', 'Drop image to upload')}</div>
        <div className="mt-2 text-sm text-typography-500">{t('webDrop.dragHint', 'Drag a JPG/PNG image here')}</div>
      </div>
    </div>
  );
}

export default DragAndDropOverlay;


